import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import { getTrackerById, getTrackers } from "../trackerStorage";
import {
  createEmptyAutomation,
  deleteWorkflowFromAutomation,
  getActionLabel,
  getAutomationById,
  saveAutomation,
} from "./automationStorage";
import WorkflowEditorPanel from "./WorkflowEditorPanel";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

/**
 * Create + edit share the same layout:
 * top meta (name / tracker / enable) + left workflows + right editor.
 */
export default function AddOrEditAutomationPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isNew = !id || id === "new";

  const [form, setForm] = useState(() =>
    createEmptyAutomation(
      searchParams.get("trackerId")
        ? getTrackerById(searchParams.get("trackerId"))
        : null
    )
  );
  const [errors, setErrors] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteWorkflow, setDeleteWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);

  const selectedWorkflowParam = searchParams.get("workflow") || "";

  useEffect(() => {
    if (isNew) {
      const trackerId = searchParams.get("trackerId");
      const tracker = trackerId ? getTrackerById(trackerId) : null;
      setForm(createEmptyAutomation(tracker));
      setNotFound(false);
      return;
    }

    const doc = getAutomationById(id);
    if (doc) {
      setForm(doc);
      setNotFound(false);
      return;
    }

    setNotFound(true);
  }, [id, isNew, searchParams]);

  const trackerOptions = useMemo(() => getTrackers().filter((t) => t?.id), []);

  const workflows = form.workflows || [];

  const selectedIsNew = selectedWorkflowParam === "new";
  const selectedWorkflowId =
    selectedIsNew || !selectedWorkflowParam ? null : selectedWorkflowParam;

  // Auto-select first workflow when none selected (edit only)
  useEffect(() => {
    if (isNew || !form?.id) return;
    if (selectedWorkflowParam) return;
    if (!workflows.length) return;
    const next = new URLSearchParams(searchParams);
    next.set("workflow", workflows[0].id);
    setSearchParams(next, { replace: true });
  }, [
    isNew,
    form?.id,
    workflows,
    selectedWorkflowParam,
    searchParams,
    setSearchParams,
  ]);

  const setSelectedWorkflow = (workflowKey) => {
    const next = new URLSearchParams(searchParams);
    if (!workflowKey) next.delete("workflow");
    else next.set("workflow", workflowKey);
    setSearchParams(next, { replace: true });
  };

  const update = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if (patch.trackerId && patch.trackerId !== prev.trackerId) {
        const tracker = getTrackerById(patch.trackerId);
        next.trackerName = tracker?.name || "";
        if (!prev.name || prev.name === `${prev.trackerName} Automation`) {
          next.name = tracker?.name
            ? `${tracker.name} Automation`
            : prev.name;
        }
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => {
        next[key] = "";
      });
      return next;
    });
  };

  const validate = () => {
    const next = {};
    if (!form.name?.trim()) next.name = "Name is required";
    if (!form.trackerId) next.trackerId = "Pick a tracker";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const persistAutomation = () => {
    if (!validate()) return null;
    try {
      const saved = saveAutomation({
        ...form,
        name: form.name.trim(),
        description: (form.description || "").trim(),
        enabled: form.enabled !== false,
        status: form.enabled !== false ? "active" : "draft",
      });
      return saved;
    } catch (err) {
      enqueueSnackbar(err?.message || "Could not save automation", {
        variant: "error",
      });
      return null;
    }
  };

  const handleSave = () => {
    setSaving(true);
    const saved = persistAutomation();
    setSaving(false);
    if (!saved) return;
    enqueueSnackbar(isNew ? "Automation created" : "Automation saved", {
      variant: "success",
    });
    if (isNew && saved?.id) {
      navigate(`/tracker-automations/${saved.id}`, { replace: true });
    } else {
      setForm(saved);
    }
  };

  /** Ensure parent exists before opening workflow editor (create flow). */
  const ensureSavedThen = (workflowKey) => {
    if (form?.id) {
      setSelectedWorkflow(workflowKey);
      return;
    }
    setSaving(true);
    const saved = persistAutomation();
    setSaving(false);
    if (!saved?.id) return;
    enqueueSnackbar("Automation created", { variant: "success" });
    const qs =
      workflowKey != null
        ? `?workflow=${encodeURIComponent(workflowKey)}`
        : "";
    navigate(`/tracker-automations/${saved.id}${qs}`, { replace: true });
  };

  const handleDeleteWorkflow = () => {
    if (!deleteWorkflow?.id || !form?.id) return;
    const saved = deleteWorkflowFromAutomation(form.id, deleteWorkflow.id);
    setForm(saved || form);
    setOpenDelete(false);
    const wasSelected = selectedWorkflowId === deleteWorkflow.id;
    setDeleteWorkflow(null);
    enqueueSnackbar("Workflow deleted", { variant: "success" });
    if (wasSelected) {
      const remaining = (saved?.workflows || []).filter(
        (w) => w.id !== deleteWorkflow.id
      );
      setSelectedWorkflow(remaining[0]?.id || null);
    }
  };

  const handleAddWorkflow = () => {
    ensureSavedThen("new");
  };

  if (notFound) {
    return (
      <div className="container-fluid tracker-module-page">
        <Box sx={{ py: 4 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
            Automation not found
          </Typography>
          <button
            type="button"
            className="small-contained-outlined-button"
            onClick={() => navigate("/tracker-automations")}
          >
            Back to Automations
          </button>
        </Box>
      </div>
    );
  }

  const showEditor = Boolean(form.id) && (selectedIsNew || selectedWorkflowId);
  const title = isNew ? "Create automation" : form.name || "Automation";

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Delete this workflow?"
        handleAgree={handleDeleteWorkflow}
      />

      <div className="container-fluid tracker-module-page">
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 2.5,
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <IconButton
                size="small"
                onClick={() => navigate("/tracker-automations")}
                sx={{ p: 0.75 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography
                component="button"
                type="button"
                onClick={() => navigate("/tracker-automations")}
                sx={{
                  border: 0,
                  background: "none",
                  p: 0,
                  cursor: "pointer",
                  color: MUTED,
                  fontSize: 13,
                  lineHeight: 1.2,
                  "&:hover": { color: PRIMARY },
                }}
              >
                All automations
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 13, lineHeight: 1.2 }}>
                /
              </Typography>
              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  maxWidth: 320,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 22, md: 26 },
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {title}
            </Typography>
            {form.trackerName ? (
              <Typography sx={{ mt: 0.6, fontSize: 13, color: MUTED }}>
                Tracker · {form.trackerName}
                {form.enabled === false ? " · Disabled" : " · Enabled"}
              </Typography>
            ) : (
              <Typography sx={{ mt: 0.6, fontSize: 13, color: MUTED }}>
                Set name & tracker, then add workflows below.
              </Typography>
            )}
          </Box>

          <button
            type="button"
            className="small-contained-button"
            onClick={handleSave}
            disabled={saving}
          >
            {isNew ? "Create automation" : "Save automation"}
          </button>
        </Box>

        {/* Meta — same on create + edit */}
        <Box
          sx={{
            mb: 2,
            p: { xs: 2, md: 2.25 },
            border: `1px solid ${BORDER}`,
            borderRadius: "12px",
            bgcolor: "#fff",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 1fr auto",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          <TextField
            size="small"
            label="Name"
            required
            fullWidth
            value={form.name || ""}
            onChange={(e) => update({ name: e.target.value })}
            error={Boolean(errors.name)}
            helperText={errors.name || " "}
            sx={fieldSx}
          />
          <TextField
            select
            size="small"
            label="Tracker"
            required
            fullWidth
            value={form.trackerId || ""}
            onChange={(e) => update({ trackerId: e.target.value })}
            error={Boolean(errors.trackerId)}
            helperText={errors.trackerId || " "}
            sx={fieldSx}
            disabled={!isNew && Boolean(form.id)}
          >
            <MenuItem value="">Select tracker…</MenuItem>
            {trackerOptions.map((tracker) => (
              <MenuItem key={tracker.id} value={tracker.id}>
                {tracker.name || "Untitled"}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            sx={{ mt: { xs: 0, md: 0.5 }, ml: 0.5 }}
            control={
              <Switch
                checked={form.enabled !== false}
                onChange={(e) => update({ enabled: e.target.checked })}
                color="primary"
              />
            }
            label={
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                Enabled
              </Typography>
            }
          />
        </Box>

        {/* Workflows — same split as edit */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            height: { xs: "auto", md: "calc(100vh - 320px)" },
            minHeight: { md: 420 },
            border: `1px solid ${BORDER}`,
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: "#fff",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 280 },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              borderRight: { md: `1px solid ${BORDER}` },
              borderBottom: { xs: `1px solid ${BORDER}`, md: 0 },
              maxHeight: { xs: 260, md: "none" },
              height: { md: "100%" },
              bgcolor: "#fafbfc",
            }}
          >
            <Box
              sx={{
                px: 1.75,
                py: 1.35,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexShrink: 0,
                borderBottom: `1px solid ${BORDER}`,
                bgcolor: "#fff",
              }}
            >
              <Typography
                sx={{ fontWeight: 700, fontSize: 13.5, color: "#0f172a" }}
              >
                Workflows
              </Typography>
              <button
                type="button"
                className="small-contained-button"
                onClick={handleAddWorkflow}
                disabled={saving}
              >
                Add
              </button>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {workflows.length === 0 && !selectedIsNew ? (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: MUTED,
                    p: 2.5,
                    textAlign: "center",
                  }}
                >
                  No workflows yet.
                </Typography>
              ) : (
                <>
                  {selectedIsNew ? (
                    <Box
                      sx={{
                        px: 1.75,
                        py: 1.25,
                        bgcolor: PRIMARY_SOFT,
                        borderLeft: `3px solid ${PRIMARY}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 13.5,
                          color: PRIMARY,
                        }}
                      >
                        New workflow
                      </Typography>
                    </Box>
                  ) : null}

                  {workflows.map((item) => {
                    const active =
                      !selectedIsNew && selectedWorkflowId === item.id;
                    return (
                      <Box
                        key={item.id}
                        sx={{
                          display: "flex",
                          alignItems: "stretch",
                          borderBottom: `1px solid #f1f5f9`,
                          bgcolor: active ? PRIMARY_SOFT : "transparent",
                          borderLeft: active
                            ? `3px solid ${PRIMARY}`
                            : "3px solid transparent",
                          "&:hover": {
                            bgcolor: active ? PRIMARY_SOFT : "#fff",
                          },
                        }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setSelectedWorkflow(item.id)}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            textAlign: "left",
                            border: 0,
                            background: "none",
                            cursor: "pointer",
                            px: 1.75,
                            py: 1.2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 13.5,
                              color: active ? PRIMARY : "#0f172a",
                              mb: 0.2,
                            }}
                          >
                            {item.name || "Untitled"}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              color: MUTED,
                              lineHeight: 1.35,
                            }}
                          >
                            {item.status === "active" ? "Live" : "Draft"} ·{" "}
                            {getActionLabel(item.action)}
                          </Typography>
                        </Box>
                        {form.id ? (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteWorkflow(item);
                              setOpenDelete(true);
                            }}
                            sx={{
                              alignSelf: "center",
                              mr: 0.75,
                              flexShrink: 0,
                              color: MUTED,
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        ) : null}
                      </Box>
                    );
                  })}
                </>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: { xs: 360, md: 0 },
              height: { md: "100%" },
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "#fff",
            }}
          >
            {showEditor ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <WorkflowEditorPanel
                  key={selectedIsNew ? "new" : selectedWorkflowId}
                  automation={form}
                  workflowId={selectedWorkflowId}
                  isNew={selectedIsNew}
                  onCancel={
                    selectedIsNew
                      ? () => setSelectedWorkflow(workflows[0]?.id || null)
                      : undefined
                  }
                  onSaved={(saved, workflow) => {
                    setForm(saved);
                    if (workflow?.id) {
                      setSelectedWorkflow(workflow.id);
                    }
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  minHeight: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                }}
              >
                <Box sx={{ textAlign: "center", maxWidth: 360 }}>
                  <Typography sx={{ fontSize: 14, color: MUTED, mb: 1.5 }}>
                    {isNew
                      ? "Pick name & tracker above, then add a workflow."
                      : "Select a workflow or add a new one."}
                  </Typography>
                  <button
                    type="button"
                    className="small-contained-button"
                    onClick={handleAddWorkflow}
                    disabled={saving}
                  >
                    Add workflow
                  </button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </div>
    </>
  );
}
